import { FC, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../../services/api";
import { useAlert } from "../../../context/AlertContext";
import LoadingSpinner from "../../../components/LoadingSpinner/LoadingSpinner";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";
import AVAILABLE_EDITORS from "../../../components/CodeEditors/AvailableEditors";

interface Assignment {
  title: string;
  description: string;
  dueDate: string;
  instructions: string;
  attachments?: string;
}

interface Lesson {
  _id?: string;
  title: string;
  description: string;
  videoUrl: string;
  duration: string;
  isFree: boolean;
  resources: string[];
  editor?: {
    id: string;
    name: string;
    url: string;
  };
  assignment?: Assignment;
}

interface Section {
  _id?: string;
  title: string;
  description: string;
  lessons: Lesson[];
}

interface Course {
  _id: string;
  title: string;
  content: Section[];
}

interface UploadProgress {
  sectionIndex: number;
  lessonIndex: number;
  progress: number;
}

const AddLessons: FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [isLoading, setIsLoading] = useState(true);
  const [course, setCourse] = useState<Course | null>(null);
  const [sections, setSections] = useState<Section[]>([
    { title: "", description: "", lessons: [] },
  ]);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(
    null
  );
  const [expandedLessons, setExpandedLessons] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const { data } = await api.get(`/courses/${courseId}`);
        setCourse(data);
        if (data.content && data.content.length > 0) {
          setSections(data.content);
        }
      } catch (error) {
        showAlert("Failed to fetch course details", "error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchCourse();
  }, [courseId, showAlert]);

  const handleAddSection = () => {
    console.log("Adding new section. Current sections:", sections.length);
    const newSections = [...sections, { title: "", description: "", lessons: [] }];
    console.log("New sections after adding:", newSections.length);
    setSections(newSections);
  };

  const handleAddLesson = (sectionIndex: number) => {
    console.log(`Adding lesson to section ${sectionIndex}. Current lessons:`, sections[sectionIndex]?.lessons.length || 0);
    const newSections = [...sections];
    newSections[sectionIndex].lessons.push({
      title: "",
      description: "",
      videoUrl: "",
      duration: "0",
      isFree: false,
      resources: [],
    });
    console.log(`New lessons count for section ${sectionIndex}:`, newSections[sectionIndex].lessons.length);
    setSections(newSections);
  };

  const handleSectionChange = (
    index: number,
    field: keyof Section,
    value: string | Lesson[]
  ) => {
    console.log(`Updating section ${index}, field: ${field}, value:`, value);
    const newSections = [...sections];
    if (field === "lessons") {
      newSections[index][field] = value as Lesson[];
    } else {
      newSections[index][field] = value as string;
    }
    setSections(newSections);
  };

  const handleLessonChange = (
    sectionIndex: number,
    lessonIndex: number,
    field: keyof Lesson,
    value:
      | string
      | boolean
      | string[]
      | { id: string; name: string; url: string }
      | Assignment
      | undefined
  ) => {
    const newSections = structuredClone(sections);
    // @ts-ignore
    newSections[sectionIndex].lessons[lessonIndex][field] = value;
    setSections(newSections);
  };

  const handleSubmit = async () => {
    console.log("handleSubmit called, isSaving:", isSaving);
    
    if (isSaving) {
      console.log("Already saving, returning early");
      return; // Prevent multiple submissions
    }
    
    try {
      console.log("Setting isSaving to true");
      setIsSaving(true);
      
      // console.log("Current sections:", sections);
      // console.log("Sections count:", sections.length);
      // sections.forEach((section, index) => {
      //   console.log(`Section ${index}:`, {
      //     title: section.title,
      //     description: section.description,
      //     lessonsCount: section.lessons.length,
      //     hasId: !!section._id,
      //     lessons: section.lessons.map((lesson, lessonIndex) => ({
      //       index: lessonIndex,
      //       title: lesson.title,
      //       hasId: !!lesson._id
      //     }))
      //   });
      // });
      
      // Validate sections and lessons before saving
      console.log("Validating sections...");
      sections.forEach((section, index) => {
        console.log(`Section ${index}: title="${section.title}", lessons=${section.lessons.length}`);
        section.lessons.forEach((lesson, lessonIndex) => {
          console.log(`  Lesson ${lessonIndex}: title="${lesson.title}"`);
        });
      });
      
      // Filter out empty sections and validate
      const validSections = sections.filter(section => 
        section.title.trim() && section.lessons.length > 0
      );
      
      // console.log("Valid sections after filtering:", validSections.length);
      // console.log("Valid sections details:", validSections.map(s => ({
      //   title: s.title,
      //   lessonsCount: s.lessons.length,
      //   hasId: !!s._id
      // })));
      
      if (validSections.length === 0) {
        console.log("Validation failed - no valid sections found");
        showAlert("Please add at least one section with a title and at least one lesson", "error");
        return;
      }
      
      // Check for sections with titles but no lessons
      const sectionsWithoutLessons = sections.filter(section => 
        section.title.trim() && section.lessons.length === 0
      );
      
      console.log("Sections without lessons:", sectionsWithoutLessons.length);
      if (sectionsWithoutLessons.length > 0) {
        // console.log("Found sections without lessons:", sectionsWithoutLessons.map(s => ({
        //   title: s.title,
        //   lessonsCount: s.lessons.length
        // })));
        showAlert(`Please add at least one lesson to the following sections: ${sectionsWithoutLessons.map(s => s.title).join(", ")}`, "error");
        return;
      }
      
      console.log("Validation passed - proceeding with save");
      
      console.log("Starting save process with valid sections:", validSections.length);
      
      // Check if this is an existing course with content (update) or new course (add)
      const isUpdating = course && course.content && course.content.length > 0;
      
      // console.log("isUpdating:", isUpdating);
      // console.log("course:", course);
      // console.log("course.content:", course?.content);
      
      if (isUpdating) {
        console.log("Processing as UPDATE - using PATCH endpoints");
        // Update existing course content using PATCH endpoints
        for (const section of validSections) {
          console.log(`Processing section: "${section.title}", has _id: ${!!section._id}`);
          if (section._id) {
            // Update existing section
            const sectionPayload = {
              title: section.title,
              description: section.description,
            };
            
            try {
              await api.patch(`/courses/${courseId}/sections/${section._id}`, sectionPayload);
            } catch (error) {
              console.warn(`Failed to update section ${section._id}:`, error);
              showAlert(`Failed to update section "${section.title}"`, "error");
            }
          } else {
            // Create new section with lessons included
            const newSectionPayload = {
              title: section.title,
              description: section.description,
              courseId: courseId,
              lessons: section.lessons.map((lesson) => ({
                title: lesson.title,
                description: lesson.description,
                videoUrl: lesson.videoUrl || "",
                duration: parseInt(lesson.duration) || 0,
                isFree: lesson.isFree || false,
                isPreview: lesson.isFree || false,
                resources: lesson.resources || [],
                tools: lesson.editor ? [lesson.editor.name] : [],
                assignment: lesson.assignment || undefined,
              })),
            };
            
            console.log(`Section payload for "${section.title}":`, newSectionPayload);
            
            try {
              // console.log(`POSTing new section with lessons to /courses/${courseId}/add/lessons`);
              const { data: newSection } = await api.post(`/courses/${courseId}/add/lessons`, newSectionPayload);
              // console.log(`Created new section "${section.title}" with lessons:`, newSection);
              // Mark section as processed since it's created with lessons
              section._id = newSection._id || 'processed';
              // Mark lessons as processed
              section.lessons.forEach((lesson, index) => {
                if (newSection.lessons && newSection.lessons[index]) {
                  lesson._id = newSection.lessons[index]._id;
                }
              });
            } catch (error: any) {
              // console.error(`Failed to create new section with lessons:`, error);
              // console.error(`Error response:`, error.response?.data);
              // console.error(`Error status:`, error.response?.status);
              // console.error(`Error message:`, error.response?.statusText);
              showAlert(`Failed to create section "${section.title}": ${error.response?.data?.message || error.message}`, "error");
              continue; // Skip this section and its lessons if creation fails
            }
          }
          
          // Only process lessons if section was not created with lessons (existing sections)
          if (section._id && section._id !== 'processed') {
            console.log(`Processing lessons for existing section "${section.title}"`);
            for (const lesson of section.lessons) {
              if (lesson._id) {
                // Update existing lesson using PATCH endpoint
                const lessonPayload = {
                  title: lesson.title,
                  description: lesson.description,
                  videoUrl: lesson.videoUrl || "",
                  duration: parseInt(lesson.duration) || 0,
                  isFree: lesson.isFree || false,
                  isPreview: lesson.isFree || false, // Map isFree to isPreview
                  resources: lesson.resources || [],
                  tools: lesson.editor ? [lesson.editor.name] : [],
                  assignment: lesson.assignment || undefined,
                };
                
                console.log(`Updating lesson "${lesson.title}" with videoUrl:`, lesson.videoUrl);
                
                try {
                  await api.patch(`/courses/${courseId}/sections/${section._id}/lessons/${lesson._id}`, lessonPayload);
                  console.log(`Successfully updated lesson "${lesson.title}"`);
                } catch (error) {
                  console.warn(`Failed to update lesson ${lesson._id}:`, error);
                  showAlert(`Failed to update lesson "${lesson.title}"`, "error");
                }
              } else {
                // Create new lesson if it doesn't have an ID
                const newLessonPayload = {
                  title: lesson.title,
                  description: lesson.description,
                  videoUrl: lesson.videoUrl || "",
                  duration: parseInt(lesson.duration) || 0,
                  isFree: lesson.isFree || false,
                  isPreview: lesson.isFree || false,
                  resources: lesson.resources || [],
                  tools: lesson.editor ? [lesson.editor.name] : [],
                  assignment: lesson.assignment || undefined,
                  sectionId: section._id,
                  courseId: courseId,
                };
                
                console.log(`Creating new lesson "${lesson.title}" in section ${section._id}`);
                
                try {
                  const { data: newLesson } = await api.post(`/courses/${courseId}/sections/${section._id}/lessons`, newLessonPayload);
                  lesson._id = newLesson._id;
                  console.log(`Successfully created lesson "${lesson.title}" with ID:`, newLesson._id);
                } catch (error) {
                  console.warn(`Failed to create new lesson:`, error);
                  showAlert(`Failed to create lesson "${lesson.title}"`, "error");
                }
              }
            }
          } else {
            console.log(`Skipping lesson processing for section "${section.title}" - already processed with section creation`);
          }
        }
        
        showAlert("Course updated successfully", "success");
      } else {
        console.log("Processing as NEW COURSE - using POST endpoints");
        // Add new lessons to course (for new courses without existing content)
        for (const section of validSections) {
          console.log(`Creating section: "${section.title}" with ${section.lessons.length} lessons`);
        const payload = {
          title: section.title,
          description: section.description,
            sectionPosition: validSections.indexOf(section),
          lessons: section.lessons.map((lesson) => ({
            title: lesson.title,
            description: lesson.description,
            videoUrl: lesson.videoUrl || "",
            duration: lesson.duration,
            isFree: lesson.isFree,
            resources: lesson.resources || [],
            tools: lesson.editor || undefined,
            assignment: lesson.assignment || undefined,
            sectionId: section._id,
            courseId: courseId,
          })),
        };

          console.log(`POSTing to /courses/${courseId}/add/lessons with payload:`, payload);
          const response = await api.post(`/courses/${courseId}/add/lessons`, payload);
          console.log(`Section "${section.title}" created successfully:`, response.data);
      }

        console.log("All sections created successfully");
      showAlert("Lessons added successfully", "success");
      }

      navigate("/admin/courses");
    } catch (error: any) {
      console.error("Save failed:", error);
      showAlert(
        error.response?.data?.message || "Failed to save course changes",
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleVideoUpload = async (
    sectionIndex: number,
    lessonIndex: number,
    file: File
  ) => {
    try {
      const formData = new FormData();
      formData.append("video", file);

      const { data } = await api.post("/courses/upload/video", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total ?? 0)
          );
          setUploadProgress({ sectionIndex, lessonIndex, progress });
        },
      });

      //Update the lesson's videoUrl using structuredClone to maintain state
      const newSections = structuredClone(sections);
      newSections[sectionIndex].lessons[lessonIndex].videoUrl = data.videoUrl;
      setSections(newSections);

      console.log(`Video uploaded successfully. URL set to:`, data.videoUrl);
      console.log(`Lesson ${lessonIndex + 1} in section ${sectionIndex + 1} videoUrl:`, newSections[sectionIndex].lessons[lessonIndex].videoUrl);

      setUploadProgress(null);
      showAlert("Video uploaded successfully", "success");
    } catch (error) {
      showAlert("Failed to upload video", "error");
      setUploadProgress(null);
    }
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(sections);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setSections(items);
  };

  const handleRemoveLesson = async (
    sectionIndex: number,
    lessonIndex: number
  ) => {
    try {
      const sectionId = sections[sectionIndex]._id;
      const lessonId = sections[sectionIndex].lessons[lessonIndex]._id;

      if (!sectionId || !lessonId) {
        // If not saved yet, just remove from state
        const newSections = structuredClone(sections);
        newSections[sectionIndex].lessons.splice(lessonIndex, 1);
        setSections(newSections);
        return;
      }

      // Call API to delete lesson
      await api.delete(
        `/courses/${courseId}/sections/${sectionId}/lessons/${lessonId}`
      );

      // Update state after successful deletion
      const newSections = structuredClone(sections);
      newSections[sectionIndex].lessons.splice(lessonIndex, 1);
      setSections(newSections);
      showAlert("Lesson deleted successfully", "success");
    } catch (error) {
      showAlert("Failed to delete lesson", "error");
    }
  };

  const handleRemoveSection = async (index: number) => {
    try {
      const sectionId = sections[index]._id; // Get section ID
      if (!sectionId) {
        // If section hasn't been saved yet, just remove from state
        const newSections = sections.filter((_, i) => i !== index);
        setSections(newSections);
        return;
      }

      // Call API to delete section
      await api.delete(`/courses/${courseId}/sections/${sectionId}`);

      // Update state after successful deletion
      const newSections = sections.filter((_, i) => i !== index);
      setSections(newSections);
      showAlert("Section deleted successfully", "success");
    } catch (error) {
      showAlert("Failed to delete section", "error");
    }
  };

  // Helper functions for managing expanded lessons
  const toggleLessonExpansion = (sectionIndex: number, lessonIndex: number) => {
    const lessonKey = `${sectionIndex}-${lessonIndex}`;
    const newExpandedLessons = new Set(expandedLessons);
    
    if (newExpandedLessons.has(lessonKey)) {
      newExpandedLessons.delete(lessonKey);
    } else {
      newExpandedLessons.add(lessonKey);
    }
    
    setExpandedLessons(newExpandedLessons);
  };

  const isLessonExpanded = (sectionIndex: number, lessonIndex: number) => {
    const lessonKey = `${sectionIndex}-${lessonIndex}`;
    return expandedLessons.has(lessonKey);
  };

  const handleSaveLesson = async (
    sectionIndex: number,
    lessonIndex: number
  ) => {
    try {
      const section = sections[sectionIndex];
      const lesson = section.lessons[lessonIndex];

      const payload = {
        title: lesson.title,
        description: lesson.description,
        videoUrl: lesson.videoUrl,
        duration: lesson.duration,
        isFree: lesson.isFree,
        resources: lesson.resources || [],
        editor: lesson.editor || undefined,
      };

      const { data } = await api.post(
        `/courses/${courseId}/sections/${section._id}/lessons`,
        payload
      );

      // Update lesson with returned data (including _id)
      const newSections = structuredClone(sections);
      newSections[sectionIndex].lessons[lessonIndex] = {
        ...lesson,
        _id: data._id,
        videoUrl: lesson.videoUrl, // Preserve the uploaded video URL
      };
      setSections(newSections);

      showAlert("Lesson saved successfully", "success");
    } catch (error) {
      showAlert("Failed to save lesson", "error");
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Add Lessons to {course?.title}</h1>
        <p className="text-gray-600">
          Organize your course content by sections
        </p>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="sections" isDropDisabled={false}>
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="space-y-6"
            >
              {sections.map((section, index) => (
                <Draggable
                  key={`section-${index}`}
                  draggableId={`section-${index}`}
                  index={index}
                >
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    >
                      <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
                        <div className="flex justify-between items-center">
                          <div
                            {...provided.dragHandleProps}
                            className="cursor-move p-2 hover:bg-gray-100 rounded"
                          >
                            ⋮⋮
                          </div>
                          <span className="text-sm text-gray-500">
                            Section {index + 1}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Section Title
                            </label>
                            <input
                              type="text"
                              value={section.title}
                              onChange={(e) =>
                                handleSectionChange(
                                  index,
                                  "title",
                                  e.target.value
                                )
                              }
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            />
                            {section.title.trim() && section.lessons.length === 0 && (
                              <p className="mt-1 text-sm text-amber-600">
                                ⚠️ This section needs at least one lesson to be saved
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Section Description
                            </label>
                            <input
                              type="text"
                              value={section.description}
                              onChange={(e) =>
                                handleSectionChange(
                                  index,
                                  "description",
                                  e.target.value
                                )
                              }
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            />
                          </div>
                        </div>

                        <div className="space-y-4">
                          {section.lessons.map((lesson, lessonIndex) => {
                            const isExpanded = isLessonExpanded(index, lessonIndex);
                            return (
                            <div
                              key={lessonIndex}
                                className="border rounded-lg bg-gray-50"
                              >
                                {/* Lesson Header - Always Visible */}
                                <div className="flex justify-between items-center p-4 border-b border-gray-200">
                                  <div className="flex items-center space-x-3">
                                    <button
                                      type="button"
                                      onClick={() => toggleLessonExpansion(index, lessonIndex)}
                                      className="flex items-center space-x-2 text-left hover:text-blue-600 transition-colors"
                                    >
                                      <div className={`transform transition-transform duration-200 ${
                                        isExpanded ? 'rotate-90' : 'rotate-0'
                                      }`}>
                                        <svg
                                          className="w-4 h-4"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 5l7 7-7 7"
                                          />
                                        </svg>
                                      </div>
                                      <div>
                                        <h3 className="text-sm font-medium text-gray-900">
                                          {lesson.title || `Lesson ${lessonIndex + 1}`}
                                </h3>
                                        {lesson.duration && (
                                          <p className="text-xs text-gray-500">
                                            {lesson.duration} minutes
                                          </p>
                                        )}
                                      </div>
                                    </button>
                                  </div>
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleSaveLesson(index, lessonIndex)
                                    }
                                    className={`text-green-600 hover:text-green-800 p-1 ${
                                      lesson._id ? "hidden" : "" // Hide if lesson already exists
                                    }`}
                                  >
                                    <svg
                                      className="w-5 h-5"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M5 13l4 4L19 7"
                                      />
                                    </svg>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleRemoveLesson(index, lessonIndex)
                                    }
                                    className="text-red-600 hover:text-red-800 p-1"
                                  >
                                    <svg
                                      className="w-5 h-5"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                      />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                                
                                {/* Collapsible Lesson Content */}
                                {isExpanded && (
                                  <div className="p-4 space-y-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    Lesson Title
                                  </label>
                                  <input
                                    type="text"
                                    value={lesson.title}
                                    onChange={(e) =>
                                      handleLessonChange(
                                        index,
                                        lessonIndex,
                                        "title",
                                        e.target.value
                                      )
                                    }
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    Description
                                  </label>
                                  <textarea
                                    value={lesson.description}
                                    onChange={(e) =>
                                      handleLessonChange(
                                        index,
                                        lessonIndex,
                                        "description",
                                        e.target.value
                                      )
                                    }
                                    rows={3}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    placeholder="Brief description of the lesson content..."
                                  />
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    Duration (minutes)
                                  </label>
                                  <input
                                    type="number"
                                    value={lesson.duration}
                                    onChange={(e) =>
                                      handleLessonChange(
                                        index,
                                        lessonIndex,
                                        "duration",
                                        e.target.value
                                      )
                                    }
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                  />
                                </div>
                                <div className="flex items-center mt-6">
                                  <input
                                    type="checkbox"
                                    checked={lesson.isFree}
                                    onChange={(e) =>
                                      handleLessonChange(
                                        index,
                                        lessonIndex,
                                        "isFree",
                                        e.target.checked
                                      )
                                    }
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                  />
                                  <label className="ml-2 block text-sm text-gray-900">
                                    Free Preview
                                  </label>
                                </div>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700">
                                  Video Upload
                                </label>
                                <input
                                  type="file"
                                  accept="video/*"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      handleVideoUpload(
                                        index,
                                        lessonIndex,
                                        file
                                      );
                                    }
                                  }}
                                  className="mt-1 block w-full"
                                />
                                {uploadProgress &&
                                  uploadProgress.sectionIndex === index &&
                                  uploadProgress.lessonIndex ===
                                    lessonIndex && (
                                    <div className="mt-2">
                                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                                        <div
                                          className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                                          style={{
                                            width: `${uploadProgress.progress}%`,
                                          }}
                                        />
                                      </div>
                                      <p className="text-sm text-gray-600 mt-1">
                                        Uploading: {uploadProgress.progress}%
                                      </p>
                                    </div>
                                  )}
                                
                                {/* Show current video URL if available */}
                                {lesson.videoUrl && (
                                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                                    <p className="text-sm text-green-800 font-medium">
                                      ✓ Video uploaded successfully
                                    </p>
                                    <p className="text-xs text-green-600 mt-1 break-all">
                                      {lesson.videoUrl}
                                      </p>
                                    </div>
                                  )}
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700">
                                  Code Editor Tool
                                </label>
                                <select
                                  value={lesson.editor?.id || ""}
                                  onChange={(e) => {
                                    const selectedEditor =
                                      AVAILABLE_EDITORS.find(
                                        (editor) => editor.id === e.target.value
                                      );
                                    handleLessonChange(
                                      index,
                                      lessonIndex,
                                      "editor",
                                      selectedEditor || undefined
                                    );
                                  }}
                                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                >
                                  <option value="">No editor</option>
                                  {AVAILABLE_EDITORS.map((editor) => (
                                    <option key={editor.id} value={editor.id}>
                                      {editor.name}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Assignment
                                </label>
                                <div className="space-y-4 p-4 border rounded-lg">
                                  <div>
                                    <label className="block text-sm text-gray-600">
                                      Title
                                    </label>
                                    <input
                                      type="text"
                                      value={lesson.assignment?.title || ""}
                                      onChange={(e) =>
                                        handleLessonChange(
                                          index,
                                          lessonIndex,
                                          "assignment",
                                          {
                                            title: e.target.value,
                                            description:
                                              lesson.assignment?.description ||
                                              "",
                                            dueDate:
                                              lesson.assignment?.dueDate || "",
                                            instructions:
                                              lesson.assignment?.instructions ||
                                              "",
                                            attachments:
                                              lesson.assignment?.attachments,
                                          } as Assignment
                                        )
                                      }
                                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-sm text-gray-600">
                                      Description
                                    </label>
                                    <textarea
                                      value={
                                        lesson.assignment?.description || ""
                                      }
                                      onChange={(e) =>
                                        handleLessonChange(
                                          index,
                                          lessonIndex,
                                          "assignment",
                                          {
                                            ...lesson.assignment,
                                            description: e.target.value,
                                          } as Assignment
                                        )
                                      }
                                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                      rows={3}
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-sm text-gray-600">
                                      Due Date
                                    </label>
                                    <input
                                      type="datetime-local"
                                      value={
                                        lesson.assignment?.dueDate
                                          ? new Date(lesson.assignment.dueDate)
                                              .toISOString()
                                              .slice(0, 16)
                                          : ""
                                      }
                                      onChange={(e) =>
                                        handleLessonChange(
                                          index,
                                          lessonIndex,
                                          "assignment",
                                          {
                                            ...lesson.assignment,
                                            dueDate: new Date(
                                              e.target.value
                                            ).toISOString(),
                                          } as Assignment
                                        )
                                      }
                                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-sm text-gray-600">
                                      Instructions
                                    </label>
                                    <textarea
                                      value={
                                        lesson.assignment?.instructions || ""
                                      }
                                      onChange={(e) =>
                                        handleLessonChange(
                                          index,
                                          lessonIndex,
                                          "assignment",
                                          {
                                            ...lesson.assignment,
                                            instructions: e.target.value,
                                          } as Assignment
                                        )
                                      }
                                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                      rows={4}
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-sm text-gray-600">
                                      Attachments
                                    </label>
                                    <input
                                      type="file"
                                      onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                          const formData = new FormData();
                                          formData.append("file", file);
                                          try {
                                            const { data } = await api.post(
                                              "/courses/upload/file",
                                              formData,
                                              {
                                                headers: {
                                                  "Content-Type":
                                                    "multipart/form-data",
                                                },
                                              }
                                            );
                                            handleLessonChange(
                                              index,
                                              lessonIndex,
                                              "assignment",
                                              {
                                                title:
                                                  lesson.assignment?.title ||
                                                  "",
                                                description:
                                                  lesson.assignment
                                                    ?.description || "",
                                                dueDate:
                                                  lesson.assignment?.dueDate ||
                                                  "",
                                                instructions:
                                                  lesson.assignment
                                                    ?.instructions || "",
                                                attachments: data.fileUrl,
                                              } as Assignment
                                            );
                                            showAlert(
                                              "File uploaded successfully",
                                              "success"
                                            );
                                          } catch (error) {
                                            showAlert(
                                              "Failed to upload file",
                                              "error"
                                            );
                                          }
                                        }
                                      }}
                                      className="mt-1 block w-full"
                                    />
                                    {lesson.assignment?.attachments && (
                                      <a
                                        href={lesson.assignment.attachments}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm text-blue-600 hover:text-blue-800 mt-1 inline-block"
                                      >
                                        View Attachment
                                      </a>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700">
                                  Resources
                                </label>
                                <div className="space-y-2">
                                  <input
                                    type="file"
                                    onChange={async (e) => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        const formData = new FormData();
                                        formData.append("file", file);
                                        try {
                                          const { data } = await api.post(
                                            "/courses/upload/file",
                                            formData,
                                            {
                                              headers: {
                                                "Content-Type":
                                                  "multipart/form-data",
                                              },
                                            }
                                          );
                                          handleLessonChange(
                                            index,
                                            lessonIndex,
                                            "resources",
                                            [
                                              ...(lesson.resources || []),
                                              data.fileUrl,
                                            ]
                                          );
                                          showAlert(
                                            "Resource uploaded successfully",
                                            "success"
                                          );
                                        } catch (error) {
                                          showAlert(
                                            "Failed to upload resource",
                                            "error"
                                          );
                                        }
                                      }
                                    }}
                                    className="mt-1 block w-full"
                                  />
                                  {lesson.resources &&
                                    lesson.resources.length > 0 && (
                                      <div className="mt-2 space-y-1">
                                        {lesson.resources.map(
                                          (resource, idx) => (
                                            <div
                                              key={idx}
                                              className="flex items-center justify-between"
                                            >
                                              <a
                                                href={resource}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-sm text-blue-600 hover:text-blue-800"
                                              >
                                                {resource.split("/").pop()}
                                              </a>
                                              <button
                                                onClick={() => {
                                                  const newResources =
                                                    lesson.resources.filter(
                                                      (_, i) => i !== idx
                                                    );
                                                  handleLessonChange(
                                                    index,
                                                    lessonIndex,
                                                    "resources",
                                                    newResources
                                                  );
                                                }}
                                                className="text-red-600 hover:text-red-800"
                                              >
                                                <i className="fas fa-times"></i>
                                              </button>
                                            </div>
                                          )
                                        )}
                                      </div>
                                    )}
                                </div>
                              </div>
                            </div>
                              )}
                            </div>
                            );
                          })}

                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => handleAddLesson(index)}
                              className="mt-2 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              Add Lesson
                            </button>
                            {sections.length > 1 && (
                              <button
                                type="button"
                                onClick={() => handleRemoveSection(index)}
                                className="mt-2 inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                              >
                                Remove Section
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <div className="flex justify-between">
        <button
          type="button"
          onClick={handleAddSection}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Add Section
        </button>

        <button
          onClick={() => {
            console.log("Save Changes button clicked");
            handleSubmit();
          }}
          disabled={isSaving}
          className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
            isSaving 
              ? "bg-gray-400 cursor-not-allowed" 
              : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {isSaving ? (
            <>
              <LoadingSpinner size="small" color="#ffffff" />
              <span className="ml-2">Saving...</span>
            </>
          ) : (
            "Save Changes"
          )}
        </button>
      </div>
    </div>
  );
};

export default AddLessons;
